import React, { useState } from 'react';
import { Mail, Phone, MessageCircle, ChevronDown, ChevronRight, SquarePen, Trash2 } from 'lucide-react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Modal } from '../../components/common/Modal';
import { type SupplierFormData } from '../../types/supplier';
import { supplierService } from '../../services/supplierService';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { SupplierForm } from './SupplierForm';
import { PurchaseOrderMiniTable } from '../../components/purchasing/PurchaseOrderMiniTable';
import { useToast } from '../../components/common/Toast';
import { getErrorMessage } from '../../utils/error';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { Pagination } from '../../components/common/Pagination';
import { usePaginationState } from '../../hooks/usePaginationState';

export const SupplierList: React.FC = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'Active' | 'Inactive'>('all');
  const { showToast } = useToast();
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('suppliers', 'write');
  const canDelete = can('suppliers', 'delete');

  const { page, size, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  resetOnChange([searchQuery, statusFilter]);

  const { data, isLoading: loading } = useQuery({
    queryKey: ['suppliers', { page, size, search: searchQuery, status: statusFilter }],
    queryFn: () => supplierService.list({ page, size, search: searchQuery, status: statusFilter }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const suppliers = data?.data ?? [];
  const total = data?.total ?? 0;

  const refreshSuppliers = () => {
    queryClient.invalidateQueries({ queryKey: ['suppliers'] });
  };

  const handleCreate = () => {
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('purchasing.supplierList.confirmDelete'))) return;
    try {
      await supplierService.delete(id);
      refreshSuppliers();
    } catch (err: unknown) {
      const rawMessage = getErrorMessage(err, t('purchasing.supplierList.toast.deleteFailed'));
      const message =
        rawMessage.includes('violates foreign key constraint') || rawMessage.includes('SQLSTATE 23503')
          ? t('purchasing.supplierList.toast.inUse')
          : rawMessage;
      showToast(message, 'error');
    }
  };

  const handleSave = async (data: SupplierFormData) => {
    try {
      if (editingId) {
        await supplierService.update(editingId, data);
      } else {
        await supplierService.create(data);
      }
      setIsModalOpen(false);
      refreshSuppliers();
    } catch (err: unknown) {
      showToast(getErrorMessage(err, t('purchasing.supplierList.toast.saveFailed')), 'error');
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const filteredSuppliers = suppliers;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">{t('purchasing.supplierList.title')}</h1>
        {canWrite && (
          <button
            data-test-id="supplier-add-button"
            onClick={handleCreate}
            className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('purchasing.supplierList.actions.add')}
          </button>
        )}
      </div>

      <div className="flex flex-col md:flex-row flex-wrap gap-4">
        <div className="flex-1">
          <label htmlFor="supplierSearch" className="block text-sm font-medium text-gray-700">
            {t('purchasing.supplierList.filters.search')}
          </label>
          <input
            data-test-id="supplier-search-input"
            type="text"
            id="supplierSearch"
            placeholder={t('purchasing.supplierList.filters.searchPlaceholder')}
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
          />
        </div>
        <div className="w-full md:w-48">
          <label className="block text-sm font-medium text-gray-700">{t('purchasing.supplierList.filters.status')}</label>
          <select
            data-test-id="supplier-status-filter"
            value={statusFilter}
            onChange={event => setStatusFilter(event.target.value as 'all' | 'Active' | 'Inactive')}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-white p-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="all">{t('purchasing.supplierList.status.all')}</option>
            <option value="Active">{t('purchasing.supplierList.status.active')}</option>
            <option value="Inactive">{t('purchasing.supplierList.status.inactive')}</option>
          </select>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <LoadingSpinner text={t('purchasing.supplierList.loading')} />
        ) : filteredSuppliers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('purchasing.supplierList.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
              <tr>
                <th className="w-8 px-6 py-3"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('purchasing.supplierList.table.name')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('purchasing.supplierList.table.email')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('purchasing.supplierList.table.phone')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('purchasing.supplierList.table.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('purchasing.supplierList.table.internalNotes')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('purchasing.supplierList.table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredSuppliers.map(supplier => (
                <React.Fragment key={supplier.id}>
                  <tr className="hover:bg-gray-50" data-test-id={`supplier-row-${supplier.id}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <button 
                        data-test-id={`supplier-expand-button-${supplier.id}`}
                        onClick={() => toggleExpand(supplier.id)}
                        className="p-1 hover:bg-gray-200 rounded-full focus:outline-none"
                      >
                        {expandedId === supplier.id ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{supplier.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {supplier.email ? (
                        <a 
                          href={`mailto:${supplier.email}`} 
                          className="text-primary-600 hover:text-primary-900 flex items-center gap-2 hover:underline"
                        >
                          <Mail className="h-4 w-4" />
                          {supplier.email}
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {supplier.phone ? (
                        <div className="flex items-center gap-2">
                          <span>{supplier.phone}</span>
                          <div className="flex gap-1">
                            <a 
                              href={`https://wa.me/${supplier.phone.replace(/\D/g, '')}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-green-600 hover:text-green-800 p-1 rounded-full hover:bg-green-50"
                              title={t('purchasing.supplierList.actions.openWhatsApp')}
                            >
                              <MessageCircle className="h-4 w-4" />
                            </a>
                            <a 
                              href={`tel:${supplier.phone}`}
                              className="text-blue-600 hover:text-blue-800 p-1 rounded-full hover:bg-blue-50"
                              title={t('purchasing.supplierList.actions.call')}
                            >
                              <Phone className="h-4 w-4" />
                            </a>
                          </div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {supplier.status === 'Active' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {t('purchasing.supplierList.status.active')}
                        </span>
                      ) : supplier.status === 'Inactive' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          {t('purchasing.supplierList.status.inactive')}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 truncate max-w-xs">{supplier.internalNotes || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end items-center gap-2">
                        <button
                          data-test-id={`supplier-edit-button-${supplier.id}`}
                          onClick={() => handleEdit(supplier.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md transition-all duration-200"
                        >
                          <SquarePen className="w-4 h-4" />
                          <span>{canWrite ? t('purchasing.supplierList.actions.edit') : t('common.view')}</span>
                        </button>

                        {canDelete && (
                          <button
                            data-test-id={`supplier-delete-button-${supplier.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(supplier.id);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-all duration-200"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>{t('purchasing.supplierList.actions.delete')}</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === supplier.id && (
                    <tr>
                      <td colSpan={7} className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                        <PurchaseOrderMiniTable supplierId={supplier.id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
        <Pagination page={page} pageSize={size} total={total} onPageChange={setPage} onPageSizeChange={setSize} isLoading={loading} />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingId ? t('purchasing.supplierList.modal.editTitle') : t('purchasing.supplierList.modal.addTitle')}
        maxWidth= "xl"
      >
        <SupplierForm
          initialData={suppliers.find(s => s.id === editingId)}
          onSubmit={handleSave}
          onCancel={() => setIsModalOpen(false)}
          existingCodes={suppliers.filter(s => s.id !== editingId).map(s => s.code || '')}
          readOnly={!canWrite}
        />
      </Modal>
    </div>
  );
};
